
import { Component, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import Loading from "vue-loading-overlay";
import { EventBus } from "./event-bus";
import { Locale } from "./store";

import "vue-loading-overlay/dist/vue-loading.css";

import { ICountryConfig, User } from "@/entities";
import { defineRulesFor } from "@/services/auth/ability";
import AsyncComputed from "vue-async-computed-decorator";
import PModal from "@/components/look/PModal.vue";
import SessionExpiredModal from "@/components/SessionExpiredModal.vue";
import DisclaimerModal from "@/components/DisclaimerModal.vue";
import UsersGuideModal from "@/components/UsersGuideModal.vue";
import AppIdle from "@app-idle/core";

@Component({
  name: "app",
  components: {
    SessionExpiredModal,
    DisclaimerModal,
    UsersGuideModal,
    Loading,
    PModal,
  },
})
export default class App extends Vue {
  @Getter("getSignedIn") getSignedIn!: boolean;
  @Getter("getLoggedUser") getLoggedUser!: User;
  @Getter("getDisclaimer") getDisclaimer!: string;
  @Getter("getUsersGuide") getUsersGuide!: string;
  @Getter("getCountryConfig") countryConfig!: ICountryConfig | undefined;
  @Action("setCurrentLocale") setCurrentLocale!: (locale: string) => void;

  accepted: boolean = localStorage.getItem("acceptCookies") === "true";
  loadings = 0;
  idle: AppIdle = new AppIdle({
    timeout: 1800000,
    onIdle: () => this.$bvModal.show("sessionExpiredModal"),
    onCancel: () => this.$bvModal.hide("sessionExpiredModal"),
    events: ["mousemove", "keydown", "mousedown", "touchstart", "wheel"], // events that cancel the idle state
    multi: true, // sets whether the idle state can be cancelled from multiple tabs/windows in one app.
    storageKey: "AppIdle", // localStorage key used for notifying other tabs/windows
    eventTarget: window, //the DOM target to attach event listeners that cancel the idle state
  });

  constructor() {
    super();
  }

  get isLoading(): boolean {
    return this.loadings > 0;
  }

  async created(): Promise<void> {
    this.setCurrentLocale(this.$i18n.locale);

    Vue.$axios.interceptors.request.use(
      (config) => {
        this.loadings++;
        return config;
      },
      (error) => {
        this.loadings--;
        return Promise.reject(error);
      }
    );

    Vue.$axios.interceptors.response.use(
      (response) => {
        this.loadings--;
        return response;
      },
      (error) => {
        this.loadings--;
        return Promise.reject(error);
      }
    );

    // Add loading interceptors to $axiosSilentLoading (without error modals, but with loading)
    Vue.$axiosSilentLoading.interceptors.request.use(
      (config) => {
        this.loadings++;
        return config;
      },
      (error) => {
        this.loadings--;
        return Promise.reject(error);
      }
    );

    Vue.$axiosSilentLoading.interceptors.response.use(
      (response) => {
        this.loadings--;
        return response;
      },
      (error) => {
        this.loadings--;
        return Promise.reject(error);
      }
    );
  }

  mounted(): void {
    EventBus.$on("language-change", (newLocale: Locale) => {
      {
        this.$i18n.locale = newLocale;
        this.setCurrentLocale(newLocale);
        document.cookie = `LOCALE=${newLocale}; path=/`;
        const html = document.documentElement;
        html.setAttribute("lang", newLocale);
      }
    });

    this.idle.start();
  }

  acceptCookies(): void {
    localStorage.setItem("acceptCookies", "true");
    this.accepted = true;
  }

  @AsyncComputed()
  async updateAbilitys(): Promise<void> {
    this.$ability.update(defineRulesFor(this.getLoggedUser, this.countryConfig));
  }
}
